import { faPlus, faRetweet, faTrash } from '@fortawesome/free-solid-svg-icons'
import {
  LanguageCode,
  MediaType,
  QuizCategory,
  QuizVisibility,
} from '@klurigo/common'
import type { FC } from 'react'
import { useState } from 'react'

import {
  Button,
  MediaModal,
  Modal,
  ResponsiveImage,
  Stack,
  Typography,
} from '../../../../../../components'
import Select from '../../../../../../components/Select'
import Textarea from '../../../../../../components/Textarea'
import TextField from '../../../../../../components/TextField'
import {
  LanguageLabels,
  QuizCategoryLabels,
  QuizVisibilityLabels,
} from '../../../../../../models'
import type {
  QuizSettingsModel,
  QuizSettingsModelFieldChangeFunction,
  QuizSettingsValidationResult,
} from '../../../../utils/QuizSettingsDataSource'
import { getValidationErrorMessage } from '../../../../validation-rules'

import styles from './QuizSettingsModal.module.scss'

export interface QuizSettingsModalProps {
  values: QuizSettingsModel
  validation: QuizSettingsValidationResult
  onValueChange: QuizSettingsModelFieldChangeFunction
  onClose: () => void
}

const QuizSettingsModal: FC<QuizSettingsModalProps> = ({
  values: {
    title,
    description,
    imageCoverURL,
    category,
    visibility,
    languageCode,
  } = {},
  validation,
  onValueChange,
  onClose,
}) => {
  const [showMediaModal, setShowMediaModal] = useState(false)

  const handleDeleteImageCover = () => {
    onValueChange('imageCoverURL', undefined)
  }

  return (
    <Modal
      title="Settings"
      closeAction={{ label: 'Close', onClick: onClose }}
      open>
      <Stack>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <label htmlFor="quiz-title-textfield">Title</label>
          </Typography>
          <TextField
            id="quiz-title-textfield"
            type="text"
            surface="light"
            placeholder="Untitled quiz"
            value={title}
            customErrorMessage={getValidationErrorMessage(validation, 'title')}
            onChange={(value) => onValueChange('title', value as string)}
            forceValidate
          />
        </div>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <label htmlFor="quiz-description-textarea">Description</label>
          </Typography>
          <Textarea
            id="quiz-description-textarea"
            placeholder="Description"
            surface="light"
            value={description}
            customErrorMessage={getValidationErrorMessage(
              validation,
              'description',
            )}
            onChange={(value) => onValueChange('description', value as string)}
            forceValidate
          />
        </div>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <span>Image Cover</span>
          </Typography>
          {imageCoverURL && <ResponsiveImage imageURL={imageCoverURL} />}
          <Stack spacing="compact">
            <Button
              id="add-image-cover-button"
              type="button"
              surface="light"
              intent="accent"
              size="small"
              value={imageCoverURL ? 'Replace' : 'Add'}
              icon={imageCoverURL ? faRetweet : faPlus}
              onClick={() => setShowMediaModal(true)}
            />
            {imageCoverURL && (
              <Button
                id="delete-image-cover-button"
                type="button"
                surface="light"
                intent="danger"
                size="small"
                value="Delete"
                icon={faTrash}
                onClick={handleDeleteImageCover}
              />
            )}
          </Stack>
        </div>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <label htmlFor="category-select">Category</label>
          </Typography>
          <Select
            id="category-select"
            surface="light"
            values={[
              { key: 'none', value: 'none', valueLabel: 'None' },
              ...Object.values(QuizCategory).map((category) => ({
                key: category,
                value: category,
                valueLabel: QuizCategoryLabels[category],
              })),
            ]}
            value={category || 'none'}
            customErrorMessage={getValidationErrorMessage(
              validation,
              'category',
            )}
            onChange={(value) =>
              onValueChange(
                'category',
                value === 'none' ? undefined : (value as QuizCategory),
              )
            }
          />
        </div>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <label htmlFor="visibility-select">Visibility</label>
          </Typography>
          <Select
            id="visibility-select"
            surface="light"
            values={Object.values(QuizVisibility).map((visibility) => ({
              key: visibility,
              value: visibility,
              valueLabel: QuizVisibilityLabels[visibility],
            }))}
            value={visibility}
            customErrorMessage={getValidationErrorMessage(
              validation,
              'visibility',
            )}
            onChange={(value) =>
              onValueChange(
                'visibility',
                value === 'none' ? undefined : (value as QuizVisibility),
              )
            }
          />
        </div>
        <div className={styles.quizSettingsRow}>
          <Typography variant="control" noOpacity bold asChild>
            <label htmlFor="language-select">Language</label>
          </Typography>
          <Select
            id="language-select"
            surface="light"
            values={[
              { key: 'none', value: 'none', valueLabel: 'None' },
              ...Object.values(LanguageCode).map((languageCode) => ({
                key: languageCode,
                value: languageCode,
                valueLabel: LanguageLabels[languageCode],
              })),
            ]}
            value={languageCode || 'none'}
            customErrorMessage={getValidationErrorMessage(
              validation,
              'languageCode',
            )}
            onChange={(value) =>
              onValueChange(
                'languageCode',
                value === 'none' ? undefined : (value as LanguageCode),
              )
            }
          />
        </div>
      </Stack>
      {showMediaModal && (
        <MediaModal
          title="Add Image Cover"
          type={MediaType.Image}
          url={imageCoverURL}
          onChange={(value) =>
            value &&
            value.type === MediaType.Image &&
            onValueChange('imageCoverURL', value.url)
          }
          onClose={() => setShowMediaModal(false)}
          lockedType
        />
      )}
    </Modal>
  )
}

export default QuizSettingsModal
